import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormArray, FormGroup, FormControl, FormBuilder, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogModule, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA,  } from '@angular/material/dialog';
import { ValidateAldPrestationsComponent } from '../validateAldPrestations/validateAldPrestations.component';

import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';



import { StateService } from '../shared/state.service';
import * as dao from '../shared/dao';
import * as utils from '../shared/utils';
import { FormatFSECurrencyPipe, FseToFrDateFormat, FormatCurrencyPipe } from '../shared/format-currency.pipe';

export interface DialogData {
	factureFR: dao.FactureFR;
	codeALD: boolean;
	isLocation?: boolean;
}

export interface PrestationsDialogResult {
  prestations: dao.PrestationFR[];
  facturer: boolean;									// Lancer la facturation en retour de dialogue ?
  validated?: boolean;
}

@Component({
  selector: 'lib-prestations',
	standalone: true,
  imports: [MatDialogModule, MatDialogContent, MatButtonModule, MatDialogActions, MatIconModule, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, MatOptionModule, MatTableModule, FormatFSECurrencyPipe, FormatCurrencyPipe],
  templateUrl: './prestations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../bs-dialog/bs-dialog.component.scss',  './prestations.component.scss'],
})

export class PrestationsComponent {
	readonly dialogRef = inject(MatDialogRef<PrestationsComponent>);
	readonly dialogdata:DialogData = inject<DialogData>(MAT_DIALOG_DATA as any);	// 'as any' tricks the compiler
	isLocation: boolean = false;
	isChanged:boolean = false;																										// Did you change tauxRemboursementAMO, montantRemboursementAMO, montantRemboursementAMC?
	isOnePlafondC2S: boolean = false;																							// Is there at least one prestation (plafond C2S)?
  isOneRental: boolean = false;                                                 // Is there at least one rental line?
  showEPColumns: boolean = false;                                               // State variable to manage EP columns visibility (hidden by default)

	constructor(public stateService: StateService, private dialog: MatDialog) {}

	ngOnInit() {
		this.isLocation = !!this.dialogdata?.isLocation;
		this.dialogdata.factureFR.prestationFR.forEach((prestation: dao.PrestationFR) => {			// Date type conversion
      //console.log(`%c[PrestationsComponent ngOnInit] input: ${JSON.stringify(prestation, null, 2)}`, "color: violet;");
			prestation.formulePrestaAMC = undefined;	// Cleanup formules calculated by simulation
			prestation.dateDemandeEP = utils.getDateStringFromYYYYMMDD(prestation.dateDemandeEP);
			this.isOnePlafondC2S = this.isOnePlafondC2S || this.isPlafondC2S(prestation);					// Set isOnePlafondC2S
      this.isOneRental = this.isOneRental || prestation.LPP?.typePrestaLPP == 'L';           
			if (prestation.qualifDepense == undefined) { prestation.qualifDepense = ''; } 				// Selects only understand strings
			if (prestation.accordEP == undefined) { prestation.accordEP = ''; }
			if (prestation.montantRemboursementAMC != undefined && prestation.montantRemboursementAMC != '') {
				prestation.montantRemboursementAMC = ((+prestation.montantRemboursementAMC) / 100).toString();
			}
			if (prestation.montantRemboursementAMO != undefined && prestation.montantRemboursementAMO != '') {
				prestation.montantRemboursementAMO = ((+prestation.montantRemboursementAMO) / 100).toString();
			}
			if (prestation.plafondCMU != undefined && prestation.plafondCMU != '') {
				prestation.plafondCMU = ((+prestation.plafondCMU) / 100).toString();
			}
      prestation.montantHonoraires = 	prestation.LPP?.mtTotalTTC;                           // Area returns a WRONG montantHonoraires (in case [EV95] 7.02 Bénéficiaire victime d'attentat) => Force it.
      if (prestation.LPP) {
        prestation.LPP.dateDebut = utils.toFRformat(prestation.LPP.dateDebut);
        prestation.LPP.dateFin = utils.toFRformat(prestation.LPP.dateFin);
      }
		});
	}

	ngAfterViewInit() {
		// Open the ALD validation dialog AFTER PrestationsComponent's first change detection cycle.
		// ValidateAldPrestationsComponent mutates shared prestation fields (e.g. codeJustifETM) in its
		// own ngOnInit; doing it from this.ngOnInit would trigger NG0100 (ExpressionChangedAfterItHasBeenCheckedError).
		if (this.isAld()) {
			this.dialog.open(ValidateAldPrestationsComponent, {
				height: 'unset',
				maxWidth: '1024px',
				panelClass: 'dialog-container-wide',
				disableClose: true,
				data: {
					factureFR: this.dialogdata.factureFR,
					codeALD: this.dialogdata.codeALD,
				},
			});
		}
	}
	
	onChangeTauxRemboursementAMO(line:dao.PrestationFR) {	// Change Tx => change montant
		let raw = ((+(line.LPP?.tarifLPP??0)/100) * (+(line.tauxRemboursementAMO??100))/100);
		raw = Math.round(raw * 100) / 100;	// Round to 2 digits
		line.montantRemboursementAMO = raw.toString();
		this.isChanged = true;
		line.forcageAMO = 'T';
	}
	
	onChangeMontantRemboursementAMO(line:dao.PrestationFR) {	// Change Tx => change montant
		let raw = ((+(line.montantRemboursementAMO??0)) * 100) / (+(line.LPP?.tarifLPP??0)/100);
		raw = Math.round(raw * 100) / 100;	// Round to 2 digits
		line.tauxRemboursementAMO = raw.toString();
		this.isChanged = true;
		line.forcageAMO = 'M';
	}	
	
	onChangeAMCAmount(line:dao.PrestationFR) {	// Change Tx => change montant
		line.forcageAMC = true;
	}	
	
	onClose() {	// Close without invoicing
		this.dialogdata.factureFR.prestationFR.forEach((prestation: dao.PrestationFR) => {
			prestation.dateDemandeEP = utils.tofseformat(prestation.dateDemandeEP??'');
			prestation.montantRemboursementAMO = undefined;
			prestation.montantRemboursementAMC = undefined;
			prestation.tauxRemboursementAMO = undefined;
			prestation.plafondCMU = undefined;
			prestation.accordEP = undefined;
			prestation.dateDemandeEP = undefined;
      if (prestation.LPP) { // Back to FSE format
        prestation.LPP.dateDebut = utils.tofseformat(prestation.LPP?.dateDebut??'');
        prestation.LPP.dateFin = utils.tofseformat(prestation.LPP?.dateFin??'');
      }
		});		
		this.dialogRef.close({prestations:this.dialogdata.factureFR.prestationFR, facturer:false, validated:false});
	}
	
	onFacturer() {
		// Format dates using fse format - format % amounts
		this.dialogdata.factureFR.prestationFR.forEach((prestation: dao.PrestationFR) => {
			prestation.dateDemandeEP = utils.tofseformat(prestation.dateDemandeEP??'');
			if (prestation.plafondCMU != undefined && prestation.plafondCMU != '') {					// No need to detect changes. NEVER calculated by AF
				prestation.plafondCMU = Math.round((+prestation.plafondCMU) * 100).toString();
			}	
			if (prestation.forcageAMO == 'T') {		// Rate changed => clear amount
				prestation.montantRemboursementAMO = undefined;
			} else if (prestation.forcageAMO == 'M') {		// Amount changed => clear rate
				prestation.tauxRemboursementAMO = undefined;
			} else {																			// Nothing changed => clear everything
				prestation.montantRemboursementAMO = undefined;
				prestation.tauxRemboursementAMO = undefined;
			}
			
			if (prestation.forcageAMC && prestation.montantRemboursementAMC != undefined && prestation.montantRemboursementAMC != '') {
				prestation.montantRemboursementAMC = Math.round((+prestation.montantRemboursementAMC) * 100).toString();
			} else {
				prestation.montantRemboursementAMC = undefined;
			}
			
			if (prestation.montantRemboursementAMO != undefined && prestation.montantRemboursementAMO != '') {
				prestation.montantRemboursementAMO = Math.round((+prestation.montantRemboursementAMO) * 100).toString();
			}

      if (prestation.LPP) {
        prestation.LPP.dateDebut = utils.tofseformat(prestation.LPP?.dateDebut??'');
        prestation.LPP.dateFin = utils.tofseformat(prestation.LPP?.dateFin??'');
      }
		
		});		
		this.dialogRef.close({prestations:this.dialogdata.factureFR.prestationFR, facturer: !this.isLocation, validated: true});
	}	
	
	public isAld(): boolean {											// Returns patient ALD situation (FROM CV OR ADRi) 
		if (this.dialogdata?.codeALD) return true;		// CodeALD forcé sur le bénéficiaire
    return this.dialogdata?.factureFR?.sn?.isALD??false;
	}

  public isSNCF(): boolean {
    if (this.dialogdata.factureFR.beneficiaire.attestationAMO.regime === '04') return true;
    return false;
  }
	
  private static readonly C2S_CODES = ['P2D', 'P2G', 'P2B', 'P2H', 'P2J', 'V01', 'V02', 'V03', 'V04', 'M01', 'M02'];
  public isPlafondC2S(line: dao.PrestationFR): boolean {
    return PrestationsComponent.C2S_CODES.includes(line.codePresta);
  }
	
	public getacharge(line:dao.PrestationFR): number {
		//console.log(line.LPP?.mtTotalTTC + " / " + line?.montantRemboursementAMO + " / " + line?.montantRemboursementAMC);
    if (line.originePresta !== 'PS') return 0;   // Area generated line => no à charge
		let raw = (+(line.montantHonoraires??0) / 100) - (+(line.montantRemboursementAMO??0)) - +(line.montantRemboursementAMC??0);
		raw = Math.round(raw * 100) / 100;	// Round to 2 digits
		return raw;
	}

  public toggleEPColumns(): void {
    this.showEPColumns = !this.showEPColumns;
  }  
}
